import React, { useState, useRef, useEffect } from 'react';
import type { BrandConfig } from '@lds-efsp/brand-config';

type OperatorStatus = 'available' | 'busy' | 'away' | 'offline';

interface StatusOption {
  value: OperatorStatus;
  label: string;
  color: string;
  description: string;
}

const statusOptions: StatusOption[] = [
  { value: 'available', label: 'Available', color: '#10B981', description: 'Ready to take new work' },
  { value: 'busy', label: 'Busy', color: '#F59E0B', description: 'Working, limited capacity' },
  { value: 'away', label: 'Away', color: '#6B7280', description: 'Temporarily unavailable' },
  { value: 'offline', label: 'Offline', color: '#EF4444', description: 'Not working' },
];

interface StatusToggleProps {
  currentStatus: OperatorStatus;
  onStatusChange: (status: OperatorStatus) => void;
  disabled?: boolean;
  brand?: BrandConfig;
}

const StatusToggle: React.FC<StatusToggleProps> = ({
  currentStatus,
  onStatusChange,
  disabled = false,
  brand
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const currentOption = statusOptions.find(s => s.value === currentStatus) || statusOptions[0];

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close on escape
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, []);

  const handleSelect = (status: OperatorStatus): void => {
    if (status !== currentStatus && !disabled) {
      onStatusChange(status);
    }
    setIsOpen(false);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className="flex items-center gap-2 px-3 py-2 rounded-lg border transition-colors min-w-[140px]"
        style={{
          borderColor: brand?.colors?.border || '#E5E7EB',
          backgroundColor: disabled ? '#F9FAFB' : 'white',
          cursor: disabled ? 'not-allowed' : 'pointer',
        }}
      >
        <span
          className="w-2.5 h-2.5 rounded-full shrink-0"
          style={{ backgroundColor: currentOption.color }}
        />
        <span
          className="text-sm font-medium flex-1 text-left"
          style={{ color: brand?.colors?.text || '#374151' }}
        >
          {currentOption.label}
        </span>
        <svg
          className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          style={{ color: brand?.colors?.textLight || '#6B7280' }}
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <div
          className="absolute top-full left-0 mt-1 w-full min-w-[200px] rounded-lg shadow-lg border z-50 py-1"
          style={{
            backgroundColor: 'white',
            borderColor: brand?.colors?.border || '#E5E7EB',
          }}
        >
          {statusOptions.map((option) => (
            <button
              key={option.value}
              onClick={() => handleSelect(option.value)}
              className="w-full flex items-start gap-3 px-3 py-2 hover:bg-gray-50 transition-colors"
            >
              <span
                className="w-2.5 h-2.5 rounded-full mt-1.5 shrink-0"
                style={{ backgroundColor: option.color }}
              />
              <div className="flex-1 text-left">
                <div
                  className="text-sm font-medium"
                  style={{ color: brand?.colors?.text || '#374151' }}
                >
                  {option.label}
                </div>
                <div
                  className="text-xs"
                  style={{ color: brand?.colors?.textLight || '#6B7280' }}
                >
                  {option.description}
                </div>
              </div>
              {option.value === currentStatus && (
                <svg
                  className="w-4 h-4 mt-0.5"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                  style={{ color: brand?.colors?.primary || '#2563EB' }}
                >
                  <path
                    fillRule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default StatusToggle;
